import * as React from "react";
import {
  Card,
  Button,
  HTMLTable,
  Tag,
  Switch,
  Popover,
  Menu,
  MenuItem,
  NonIdealState,
} from "@blueprintjs/core";
import { css } from "@emotion/core";
import { IOrderType } from "../../../api/admin";
import { roundedHoursFromSeconds } from "../../../utils/format";
import { stickyTableHeaderCss } from "../common/adminCss";
import { ReadOnlyNotice } from "../common/ReadOnlyNotice";
import { readOnlyNoticeCopy } from "../common/readOnlyNoticeCopy";

type OrderTypeWithAssignment = IOrderType & { assignment_id: number };

function isOrderTypeDeactivated(orderType: IOrderType): boolean {
  return orderType.deleted_at !== null && orderType.deleted_at !== undefined;
}

function hasAssignmentId(
  orderType: IOrderType
): orderType is OrderTypeWithAssignment {
  return orderType.assignment_id !== undefined;
}

export function OrderTypesPanel(props: {
  orgOrderTypes: IOrderType[];
  handleAssignOrderType: () => void;
  handleDeactivateOrderType: (assignmentId: number) => void;
  handleActivateOrderType?: (assignmentId: number) => void;
  handleEditOrderTypeAssignment?: (assignmentId: number) => void;
  isActivating?: boolean;
  showDeactivated: boolean;
  onShowDeactivatedChange: (checked: boolean) => void;
  canManage: boolean;
}) {
  const {
    orgOrderTypes,
    handleAssignOrderType,
    handleDeactivateOrderType,
    handleActivateOrderType,
    handleEditOrderTypeAssignment,
    isActivating,
    showDeactivated,
    onShowDeactivatedChange,
    canManage,
  } = props;

  return (
    <Card
      css={css`
        padding: 0;
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 11px;
          background-color: var(--neutral-7);
          box-shadow: inset 0 0 1px 1px var(--neutral-5);
        `}
      >
        <h3>Order Types</h3>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 1rem;
          `}
        >
          <Switch
            css={css`
              margin: 0;
            `}
            label="Show deactivated"
            checked={showDeactivated}
            onChange={(e) => onShowDeactivatedChange(e.currentTarget.checked)}
          />
          {canManage && (
            <Button
              icon="plus"
              text="Add Order Type"
              intent="primary"
              onClick={handleAssignOrderType}
            />
          )}
        </div>
      </div>
      {!canManage && (
        <ReadOnlyNotice>{readOnlyNoticeCopy.orderTypes}</ReadOnlyNotice>
      )}
      {orgOrderTypes.length === 0 ? (
        <NonIdealState
          icon="cube"
          title="No Order Types Found"
          description="No order types are assigned to this organization."
        />
      ) : (
        <div
          css={css`
            flex: 1;
            overflow: auto;
          `}
        >
          <HTMLTable
            striped
            interactive={canManage}
            css={[stickyTableHeaderCss, css`
              width: 100%;
            `]}
          >
            <colgroup>
              <col />
              <col width="22%" />
              <col width="12%" />
              <col width="12%" />
              <col width="26%" />
              <col width="64px" />
            </colgroup>
            <thead>
              <tr>
                <th>Name</th>
                <th>Site</th>
                <th>Deadline</th>
                <th>Orders</th>
                <th>Status</th>
                <th
                  css={css`
                    width: 64px;
                  `}
                />
              </tr>
            </thead>
            <tbody>
              {orgOrderTypes.filter(hasAssignmentId).map((orderType) => {
                const isDeactivated = isOrderTypeDeactivated(orderType);
                return (
                  <tr
                    key={orderType.assignment_id}
                    onClick={
                      canManage
                        ? () => {
                            if (
                              !isDeactivated &&
                              handleEditOrderTypeAssignment
                            ) {
                              handleEditOrderTypeAssignment(
                                orderType.assignment_id
                              );
                            }
                          }
                        : undefined
                    }
                    css={css`
                      cursor: ${canManage && !isDeactivated
                        ? "pointer"
                        : "default"};
                    `}
                  >
                    <td>
                      <div
                        css={css`
                          height: 30px;
                          display: flex;
                          align-items: center;
                          color: ${isDeactivated
                            ? "var(--neutral-3)"
                            : "inherit"};
                        `}
                      >
                        {orderType.name}
                      </div>
                    </td>
                    <td>
                      <div
                        css={css`
                          height: 30px;
                          display: flex;
                          align-items: center;
                          color: ${isDeactivated
                            ? "var(--neutral-3)"
                            : "inherit"};
                        `}
                      >
                        {orderType.site_label || "No Site"}
                      </div>
                    </td>
                    <td>
                      <div
                        css={css`
                          height: 30px;
                          display: flex;
                          align-items: center;
                          color: ${isDeactivated
                            ? "var(--neutral-3)"
                            : "inherit"};
                        `}
                      >
                        {roundedHoursFromSeconds(
                          orderType.deadline_duration_seconds ?? 0
                        )}{" "}
                        h
                      </div>
                    </td>
                    <td>
                      <div
                        css={css`
                          height: 30px;
                          display: flex;
                          align-items: center;
                          color: ${isDeactivated
                            ? "var(--neutral-3)"
                            : "inherit"};
                        `}
                      >
                        {orderType.order_count || 0}
                      </div>
                    </td>
                    <td>
                      <div
                        css={css`
                          height: 30px;
                          display: flex;
                          align-items: center;
                        `}
                      >
                        <Tag
                          intent={isDeactivated ? "warning" : "success"}
                          minimal
                        >
                          {isDeactivated ? "Deactivated" : "Active"}
                        </Tag>
                      </div>
                    </td>
                    <td>
                      <div
                        onClick={(e) => e.stopPropagation()}
                        css={css`
                          display: flex;
                          justify-content: center;
                          align-items: center;
                          height: 30px;
                          width: 100%;
                        `}
                      >
                        {canManage && (
                          <Popover
                            content={
                              <Menu>
                                {!isDeactivated &&
                                  handleEditOrderTypeAssignment && (
                                  <MenuItem
                                    icon="edit"
                                    text="Edit"
                                    onClick={() =>
                                      handleEditOrderTypeAssignment(
                                        orderType.assignment_id
                                      )
                                    }
                                  />
                                )}
                                {!isDeactivated && (
                                  <MenuItem
                                    icon="disable"
                                    text={
                                      (orderType.order_count || 0) === 0
                                        ? "Delete"
                                        : "Deactivate"
                                    }
                                    intent="danger"
                                    onClick={() =>
                                      handleDeactivateOrderType(
                                        orderType.assignment_id
                                      )
                                    }
                                  />
                                )}
                                {isDeactivated && handleActivateOrderType && (
                                  <MenuItem
                                    icon="tick"
                                    text={isActivating ? "Activating..." : "Activate"}
                                    onClick={() =>
                                      handleActivateOrderType(orderType.assignment_id)
                                    }
                                    intent="success"
                                    disabled={isActivating}
                                  />
                                )}
                              </Menu>
                            }
                            placement="bottom-end"
                          >
                            <Button size="small" icon="more" />
                          </Popover>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </HTMLTable>
        </div>
      )}
    </Card>
  );
}
